import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import moment from 'moment-timezone';
import React, { useState } from 'react';

import StyledFilledTextField from './StyledFilledTextField';

const Styles = {
    datePicker: {},
    calendar: {
        border: 'solid 1px #ccc',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100000,
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
    },
    month: {
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    monthTitle: {
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        height: '30px',
    },
    dateCell: {
        backgroundColor: 'white',
        cursor: 'pointer',
        padding: '5px',
        textAlign: 'center',
        transition: 'background-color 0.2s',
        width: '20px',
        height: '20px',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    backDropRight: {
        width: '30px',
        background: 'linear-gradient(90deg, #ffffff 50%, #f7f7f7 50%)',
        height: '30px',
        position: 'relative',
    },
    backDropLeft: {
        width: '30px',
        background: 'linear-gradient(90deg, #f7f7f7 50%, #ffffff 50%)',
        height: '30px',
        position: 'relative',
    },
    backDropEmpty: {
        width: '30px',
        height: '30px',
        backgroundColor: '#ffffff',
        position: 'relative',
    },
    selectedDate: {
        backgroundColor: '#222222',
        color: 'white',
        cursor: 'pointer',
        padding: '5px',
        textAlign: 'center',
        transition: 'background-color 0.2s',
        width: '20px',
        height: '20px',
        borderRadius: '50%',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },

    inRangeDate: {
        backgroundColor: '#f7f7f7',
        color: '#222',
        cursor: 'pointer',
        padding: '5px',
        textAlign: 'center',
        transition: 'background-color 0.2s',
        width: '20px',
        height: '20px',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    monthDays: {
        display: 'flex',
        width: '210px',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
};
const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const getDaySlotStyles = (currentDate, startDate, endDate) => {
    const isDateSelected = startDate && endDate ? currentDate.isBetween(startDate, endDate, 'day', '[]') : startDate && currentDate.format('YYYY-MM-DD') === startDate.format('YYYY-MM-DD');
    if (isDateSelected && (currentDate.isSame(startDate, 'day') || currentDate.isSame(endDate, 'day'))) {
        return Styles.selectedDate;
    }
    if (isDateSelected) {
        return Styles.inRangeDate;
    }

    return Styles.dateCell;
};

const getCorrectBackdrop = (currentDate, startDate, endDate) => {
    if (currentDate.isSame(startDate, 'day') && endDate) {
        return Styles.backDropRight;
    }

    if (endDate && currentDate.isSame(endDate, 'day')) {
        return Styles.backDropLeft;
    }

    return Styles.backDropEmpty;
};

const formatDate = (date) => {
    return date.format('MM/DD/YYYY');
};

const DateRangePicker = (props) => {
    const defaultStartDate = props.defaultStartDate ? moment(props.defaultStartDate) : null;
    const [startDate, setStartDate] = useState(defaultStartDate);
    let added_days_end_date = defaultStartDate ? moment(defaultStartDate).add(props.daySpan - 1, 'days') : null;
    const [endDate, setEndDate] = useState(props.daySpan > 1 ? added_days_end_date : null);
    const [calendarEl, setCalendarEl] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
    const nextMonth = moment(currentMonth).add(1, 'month');

    const handleDateClick = (date) => {
        // nothing selected yet
        if (!startDate) {
            setStartDate(moment(date));
            setEndDate(null);
            props.onChange([date, null]);
            return;
        }
        // select the end date of the range
        if (moment(date).isAfter(startDate) && !endDate) {
            setEndDate(moment(date));
            props.onChange([startDate, date]);
            setCalendarEl(null);
            return;
        }

        // select a new start date
        if (moment(date).isBefore(startDate)) {
            setStartDate(moment(date));
            setEndDate(null);
            props.onChange([date, null]);
            return;
        }
        // range already selected, select a new start date that is later than end date
        if (startDate && endDate && moment(date).isAfter(endDate)) {
            setStartDate(moment(date));
            setEndDate(null);
            props.onChange([date, null]);
            return;
        }
        // range already selected, select a new start date
        if (startDate && endDate && moment(date).isAfter(startDate) && moment(date).isBefore(endDate)) {
            setStartDate(moment(date));
            setEndDate(null);
            props.onChange([date, null]);
            return;
        }
        return;
    };

    const handlePreviousMonth = () => {
        setCurrentMonth(prevMonth => moment(prevMonth).subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setCurrentMonth(prevMonth => moment(prevMonth).add(1, 'month'));
    };

    const renderCalendarDays = (month) => {
        const daysInMonth = month.daysInMonth();
        const firstDayOfMonth = month.clone().startOf('month');
        const firstDayOfWeek = firstDayOfMonth.day();
        let days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(
                <div style={Styles.backDropEmpty} key={`empty-${i}`}>
                    <div style={Styles.dateCell}></div>
                </div>
            );
        }

        // Add actual days of the month
        for (let i = 0; i < daysInMonth; i++) {
            let currentDate = firstDayOfMonth.clone().add(i, 'days');
            let backdrop_style = getCorrectBackdrop(currentDate, startDate, endDate);
            let dayslot_style = getDaySlotStyles(currentDate, startDate, endDate);

            days.push(
                <div style={backdrop_style} key={currentDate.format()}>
                    <div onClick={() => handleDateClick(currentDate)} style={dayslot_style}>
                        {currentDate.date()}
                    </div>
                </div>
            );
        }

        return days;
    };

    const formatInputValue = () => {
        if (!startDate) {
            return '';
        }
        if (!endDate) {
            return formatDate(startDate);
        }
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    };

    return (
        <>
            <StyledFilledTextField size="small" label="Date" fullWidth value={formatInputValue()} onClick={(ev) => setCalendarEl(ev.currentTarget)} readOnly />
            <Popper open={Boolean(calendarEl)} style={{ zIndex: 1 }} anchorEl={calendarEl} onClose={() => setCalendarEl(null)} placement="bottom-start">
                <ClickAwayListener onClickAway={() => setCalendarEl(null)}>
                    <Paper>
                        <div style={{ ...Styles.calendar, flexDirection: props.isSmallScreen ? 'column' : 'row' }}>
                            <div style={Styles.month}>
                                <div style={Styles.monthTitle}>
                                    <ChevronLeft onClick={handlePreviousMonth} style={{ cursor: 'pointer' }} />
                                    <Typography> {currentMonth.format('MMMM YYYY')}</Typography>
                                </div>
                                <div style={Styles.monthDays}>
                                    {dayLabels.map((dayLabel, index) => (
                                        <div key={index} style={{ ...Styles.backDropEmpty, key: dayLabel }}>
                                            <div style={{ ...Styles.dateCell, color: '#A8A8A8', fontSize: '90%', cursor: 'auto' }}>{dayLabel}</div>
                                        </div>
                                    ))}
                                    {renderCalendarDays(currentMonth)}
                                </div>
                            </div>
                            <div style={Styles.month}>
                                <div style={Styles.monthTitle}>
                                    <Typography> {nextMonth.format('MMMM YYYY')}</Typography>
                                    <ChevronRight onClick={handleNextMonth} style={{ cursor: 'pointer' }} />
                                </div>
                                <div style={Styles.monthDays}>
                                    {dayLabels.map((dayLabel, index) => (
                                        <div key={index} style={{ ...Styles.backDropEmpty, key: dayLabel }}>
                                            <div style={{ ...Styles.dateCell, color: '#A8A8A8', fontSize: '90%', cursor: 'auto' }}>{dayLabel}</div>
                                        </div>
                                    ))}
                                    {renderCalendarDays(nextMonth)}
                                </div>
                            </div>
                        </div>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

export default DateRangePicker;
